import { Injectable } from '@angular/core';
import {
  PDFDocument,
  PDFPage,
  degrees,
  pushGraphicsState,
  popGraphicsState,
  concatTransformationMatrix,
} from 'pdf-lib';

import { jsPDF } from 'jspdf';
import * as pdfjsLib from 'pdfjs-dist';
import { PdfjsWorkerBootstrapService } from './pdfjs-worker-bootstrap.service';

const A4_WIDTH = 595.28;
const A4_HEIGHT = 841.89;

/**
 * Transforms a landscape PDF page to portrait by rotating its content stream
 * 90 degrees clockwise and updating its dimensions (MediaBox) to portrait,
 * resetting page rotation to 0 so the rotation is baked directly into the content.
 */
export function rotateLandscapePageToPortrait(page: PDFPage, doc: PDFDocument): boolean {
  const currentRot = ((page.getRotation().angle % 360) + 360) % 360;
  const { x: originX, y: originY, width, height } = page.getMediaBox();

  const isSwapped = currentRot === 90 || currentRot === 270;
  const effW = isSwapped ? height : width;
  const effH = isSwapped ? width : height;

  if (effW <= effH) {
    return false; // Already portrait
  }

  // The new dimensions for portrait orientation:
  const newWidth = effH;
  const newHeight = effW;

  const targetClockwiseAngle = (currentRot + 90) % 360;

  let a = 1, b = 0, c = 0, d = 1, e = -originX, f = -originY;
  if (targetClockwiseAngle === 90) {
    a = 0;
    b = -1;
    c = 1;
    d = 0;
    e = -originY;
    f = width + originX;
  } else if (targetClockwiseAngle === 180) {
    a = -1;
    b = 0;
    c = 0;
    d = -1;
    e = width + 2 * originX;
    f = height + 2 * originY;
  } else if (targetClockwiseAngle === 270) {
    a = 0;
    b = 1;
    c = -1;
    d = 0;
    e = height + originY;
    f = -originX;
  }

  const pageAny = page as any;
  pageAny.node.normalize();
  pageAny.getContentStream();
  const start = pageAny.createContentStream(
    pushGraphicsState(),
    concatTransformationMatrix(a, b, c, d, e, f)
  );
  const startRef = doc.context.register(start);
  const end = pageAny.createContentStream(popGraphicsState());
  const endRef = doc.context.register(end);
  pageAny.node.wrapContentStreams(startRef, endRef);

  page.setMediaBox(0, 0, newWidth, newHeight);
  page.setCropBox(0, 0, newWidth, newHeight);
  page.setBleedBox(0, 0, newWidth, newHeight);
  page.setTrimBox(0, 0, newWidth, newHeight);
  page.setArtBox(0, 0, newWidth, newHeight);
  page.setRotation(degrees(0));
  return true;
}

@Injectable({
  providedIn: 'root',
})
export class PdfResizeService {
  constructor(private readonly pdfjsWorkerBootstrap: PdfjsWorkerBootstrapService) {}

  async convertFilesToA4(files: File[]): Promise<Uint8Array> {
    const newPdf = await PDFDocument.create();

    for (const file of files) {
      const arrayBuffer = await file.arrayBuffer();
      const originalPdf = await PDFDocument.load(arrayBuffer);
      const totalPages = originalPdf.getPageCount();

      for (let i = 0; i < totalPages; i++) {
        const [copiedPage] = await newPdf.copyPages(originalPdf, [i]);

        const originalWidth = copiedPage.getWidth();
        const originalHeight = copiedPage.getHeight();

        // Calculate the scale to fit the content inside A4 without distortion
        const scale = Math.min(
          A4_WIDTH / originalWidth,
          A4_HEIGHT / originalHeight
        );

        const scaledWidth = originalWidth * scale;
        const scaledHeight = originalHeight * scale;

        // Center the content on the A4 page
        const xOffset = (A4_WIDTH - scaledWidth) / 2;
        const yOffset = (A4_HEIGHT - scaledHeight) / 2;

        const newPage = newPdf.addPage([A4_WIDTH, A4_HEIGHT]);

        const embeddedPage = await newPdf.embedPage(copiedPage);

        // Draw the scaled page properly
        newPage.drawPage(embeddedPage, {
          x: xOffset,
          y: yOffset,
          width: scaledWidth,
          height: scaledHeight,
        });

        console.log(`Original size: ${originalWidth} x ${originalHeight}`);
        console.log(`Scaled size: ${scaledWidth} x ${scaledHeight}`);
        console.log(`Offsets: x=${xOffset}, y=${yOffset}`);
      }
    }

    return await newPdf.save();
  }

  async enforceA4Size(originalPdfBytes: Uint8Array): Promise<Uint8Array> {
    const originalPdf = await PDFDocument.load(originalPdfBytes);
    const newPdf = await PDFDocument.create();
    const copiedPages = await newPdf.copyPages(
      originalPdf,
      originalPdf.getPageIndices()
    );

    const a4Width = 595.28;
    const a4Height = 841.89;

    // Copy ALL pages in a single copyPages() call. pdf-lib creates one
    // PDFObjectCopier per call, and that copier's resource-dedup cache only
    // lives for the duration of the call — copying page-by-page in a loop
    // re-embeds any font/image shared across pages once per page, which can
    // multiply output size several times over. A single batched call lets
    // the copier dedupe shared resources across the whole document.

    copiedPages.forEach((copiedPage, i) => {
      const rot = ((copiedPage.getRotation().angle % 360) + 360) % 360;
      const {
        x: originX,
        y: originY,
        width: originalWidth,
        height: originalHeight,
      } = copiedPage.getMediaBox();

      const isSwapped = rot === 90 || rot === 270;
      const effWidth = isSwapped ? originalHeight : originalWidth;
      const effHeight = isSwapped ? originalWidth : originalHeight;

      // Calculate the scale to fit the content inside A4 without distortion
      const scale = Math.min(
        a4Width / effWidth,
        a4Height / effHeight
      );

      const scaledWidth = effWidth * scale;
      const scaledHeight = effHeight * scale;

      // Center the content on the A4 page
      const xOffset = (a4Width - scaledWidth) / 2;
      const yOffset = (a4Height - scaledHeight) / 2;

      console.log(
        `Page ${i + 1} - Original size: ${originalWidth} x ${originalHeight}, Rot: ${rot}, ` +
          `Scaled size: ${scaledWidth} x ${scaledHeight}, Offsets: x=${xOffset}, y=${yOffset}`
      );

      let a = scale;
      let b = 0;
      let c = 0;
      let d = scale;
      let e = xOffset - scale * originX;
      let f = yOffset - scale * originY;

      if (rot === 90) {
        a = 0;
        b = -scale;
        c = scale;
        d = 0;
        e = xOffset - scale * originY;
        f = yOffset + scale * (originalWidth + originX);
      } else if (rot === 180) {
        a = -scale;
        b = 0;
        c = 0;
        d = -scale;
        e = xOffset + scale * (originalWidth + originX);
        f = yOffset + scale * (originalHeight + originY);
      } else if (rot === 270) {
        a = 0;
        b = scale;
        c = -scale;
        d = 0;
        e = xOffset + scale * (originalHeight + originY);
        f = yOffset - scale * originX;
      }

      newPdf.addPage(copiedPage);

      // Resize and orient the page's own content stream in place with rotation baked in.
      const pageAny = copiedPage as any;
      pageAny.node.normalize();
      pageAny.getContentStream();
      const start = pageAny.createContentStream(
        pushGraphicsState(),
        concatTransformationMatrix(a, b, c, d, e, f)
      );
      const startRef = newPdf.context.register(start);
      const end = pageAny.createContentStream(popGraphicsState());
      const endRef = newPdf.context.register(end);
      pageAny.node.wrapContentStreams(startRef, endRef);

      copiedPage.setMediaBox(0, 0, a4Width, a4Height);
      copiedPage.setCropBox(0, 0, a4Width, a4Height);
      copiedPage.setBleedBox(0, 0, a4Width, a4Height);
      copiedPage.setTrimBox(0, 0, a4Width, a4Height);
      copiedPage.setArtBox(0, 0, a4Width, a4Height);
      copiedPage.setRotation(degrees(0));

      console.log(`Page ${i + 1} resized and added to new PDF.`);
    });

    console.log('Resizing complete');
    return await newPdf.save({ useObjectStreams: true });
  }

  async convertSingleFileToA4(file: File): Promise<File> {
    const arrayBuffer = await file.arrayBuffer();
    const resizedPdfBytes = await this.enforceA4Size(
      new Uint8Array(arrayBuffer)
    );

    return new File([resizedPdfBytes.buffer as ArrayBuffer], file.name, {
      type: file.type || 'application/pdf',
      lastModified: Date.now(),
    });
  }

  async convertFileToBase64(file: File): Promise<string> {
    var a4file = await this.convertSingleFileToA4(file);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(a4file);

      reader.onload = () => {
        resolve(reader.result as string);
      };

      reader.onerror = (error) => {
        reject(error);
      };
    });
  }

  async convertToA4PreservingAll(file: File): Promise<File> {
    await this.pdfjsWorkerBootstrap.ready;
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const newPdf = await PDFDocument.create();

    for (let i = 0; i < pdf.numPages; i++) {
      const page = await pdf.getPage(i + 1);
      const viewport = page.getViewport({ scale: 1.7 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d')!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;

      // CRITICAL: JPEG @ 0.75 quality vs PNG (50-70% smaller)
      const imgData = canvas.toDataURL('image/jpeg', 0.7);
      const newPage = newPdf.addPage([A4_WIDTH, A4_HEIGHT]);
      const jpegImage = await newPdf.embedJpg(imgData);

      const scale = Math.min(A4_WIDTH / canvas.width, A4_HEIGHT / canvas.height);
      const scaledWidth = canvas.width * scale;
      const scaledHeight = canvas.height * scale;

      const xOffset = (A4_WIDTH - scaledWidth) / 2;
      const yOffset = (A4_HEIGHT - scaledHeight) / 2;

      newPage.drawImage(jpegImage, {
        x: xOffset,
        y: yOffset,
        width: scaledWidth,
        height: scaledHeight,
      });

      if (i % 5 === 4) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }
    }
    const pdfBytes = await newPdf.save({ useObjectStreams: true });
    return new File([pdfBytes.buffer as ArrayBuffer], file.name.replace('.pdf', '_A4.pdf'), {
      type: 'application/pdf',
    });
  }

  arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 32766; // 0x7FFE, multiple of 3 and within String.fromCharCode spread limits
    let base64 = '';

    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
      const chunk = bytes.subarray(offset, offset + chunkSize);
      base64 += btoa(String.fromCharCode(...chunk));
    }

    return base64;
  }
}
